import { envConfig, Locale } from '@headless-workspace/config';
import {
    CachedRepository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import { RequestStrategy } from '@headless-workspace/data';
import { DecoderHelpers } from '@headless-workspace/utils';
import { VipBrandsValue } from '../config';
import { BasketProductQuantityValue } from './BasketProductQuantityValue';
import { BasketsDTO } from './BasketsDTO';
import { CustomerBasketContainerDTO, CustomerBasketProductDTO } from './CustomerBasketDTO';
import { CustomerBasketValue, mapCustomerBasket } from './CustomerBasketValue';

const BasketCacheConfig = {
    ttl: 604800, // 7 days
    staleWhileRevalidate: 432000, // 5 days
};

export const CACHE_SERVICE_BASKET_KEY = 'basket';

export interface BasketRepositorySpec {
    getCustomerBasket(
        locale: Locale,
        customerId: string,
        vipBrands: VipBrandsValue,
    ): Promise<Result<CustomerBasketValue>>;

    addToBasket(
        locale: Locale,
        customerId: string,
        vipBrands: VipBrandsValue,
        products: BasketProductQuantityValue[],
    ): Promise<Result<CustomerBasketValue>>;
}

export class BasketRepository extends CachedRepository implements BasketRepositorySpec {
    readonly path = '/basket/v1';

    constructor(requestStrategy: RequestStrategy) {
        super(requestStrategy.authStrategy, requestStrategy.refreshTokenStrategy, requestStrategy.queryParamStrategy);
    }

    async getCustomerBasketId(locale: Locale, customerId: string): Promise<Result<string>> {
        try {
            const params = { customerId };
            const cachedBasketId = await this.cacheService?.get<string>(locale, CACHE_SERVICE_BASKET_KEY, params);

            if (cachedBasketId?.data) {
                return ResultSuccess(cachedBasketId.data);
            }

            const basketResponse = await this.datasource.getResource(`${this.path}/baskets`, {
                customerId,
                locale,
            });

            const basketResult = BasketsDTO.parse(basketResponse);
            const basketId = basketResult.baskets[0]?.id;

            if (basketId) {
                await this.cacheService?.set(locale, CACHE_SERVICE_BASKET_KEY, params, basketId, {
                    ttl: envConfig.cacheBasketIdTtlSeconds ?? BasketCacheConfig.ttl,
                    staleWhileRevalidate:
                        envConfig.cacheBasketIdStaleTimeSeconds ?? BasketCacheConfig.staleWhileRevalidate,
                });
                return ResultSuccess(basketId);
            }

            return ResultEmpty();
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async getCustomerBasket(
        locale: Locale,
        customerId: string,
        vipBrands: VipBrandsValue,
    ): Promise<Result<CustomerBasketValue>> {
        try {
            const basketIdResult = await this.getCustomerBasketId(locale, customerId);

            if (basketIdResult.type !== 'success') {
                return basketIdResult;
            }

            const customerBasketResponse = await this.datasource.getResource(
                `${this.path}/baskets/${basketIdResult.data}`,
                { locale },
            );

            return ResultSuccess(this.mapCustomerBasketResponse(customerBasketResponse, vipBrands, locale));
        } catch (error) {
            // Delete cached basket ID on error to avoid stale cache
            this.cacheService?.del(locale, CACHE_SERVICE_BASKET_KEY, { customerId });
            // Then return failure result
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async addToBasket(
        locale: Locale,
        customerId: string,
        vipBrands: VipBrandsValue,
        products: BasketProductQuantityValue[],
    ): Promise<Result<CustomerBasketValue>> {
        try {
            const basketResponse = await this.getCustomerBasketId(locale, customerId);
            if (basketResponse.type !== 'success') {
                return ResultEmpty();
            }

            const customerBasketResponse = await this.datasource.createResource(
                `${this.path}/baskets/${basketResponse.data}/products`,
                { products },
                { locale },
            );

            return ResultSuccess(this.mapCustomerBasketResponse(customerBasketResponse, vipBrands, locale));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    private mapCustomerBasketResponse(
        customerBasketResponse: unknown,
        vipBrands: VipBrandsValue,
        locale: Locale,
    ): CustomerBasketValue {
        const basket = CustomerBasketContainerDTO.parse(customerBasketResponse);
        const basketProducts = DecoderHelpers.safeParseArray(basket.products, CustomerBasketProductDTO);
        return mapCustomerBasket({ ...basket, products: basketProducts }, vipBrands, locale);
    }
}
